/**
 * @description
 * This component handles the entire IRB submission form fields' content and logic, including:
 * - Displaying questions, instructions, warnings, and UI for document uploads.
 * - Handling validation constraints for all questions and documents.
 * - Enabling visibility dependencies for conditional fields.
 *
 * Exports from this component are used by the pages SubmissionFormDraft, SubimssionFormNeedsAction, and SubmissionFormReadOnly.
 *
 * Key features:
 * - Detailed custom validation for document fields.
 * - Reusable "FormField" definitions and rendering logic.
 * - Progressive disclosure of fields based on user input or dependencies.
 *
 * @dependencies
 * - react-hook-form: Handles form state and validations.
 * - headlessui (Listbox, RadioGroup): For styled dropdowns & radio groups.
 *
 * @functions
 * - validateDocument: Validates document fields.
 * - useFormLock: Manages form lock state to prevent multiple tabs from editing the same submission.
 *
 * @notes
 * - Pending browser files stay in the upload workspace, outside autosaved form data.
 * - Document validation requires a confirmed active database row.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useForm, FieldErrorsImpl } from 'react-hook-form';
import { Listbox } from '@headlessui/react';
import { initialSubmissionFormSections } from '../forms/initialSubmissionForm';
import { documentFormValues, isSavedDocument, USER_DOCUMENT_TYPES } from '../services/documentWorkflowService';
import { DocumentUploadWorkspace } from './DocumentUploadWorkspace';

/** Interfaces, form definitions, and dependency checks. */
export interface FormField {
  itemType: 'question' | 'warning' | 'instruction' | 'document';
  name?: string;
  label: string;
  fieldType?: string;
  options?: string[];
  placeholder?: string;
  validation?: Record<string, any>;
  dependency?: Record<string, any>;
  rows?: number;
  documentType?: string;
  allowMultiple?: boolean;
}

export interface FormSection {
  heading: string;
  description?: string;
  items: FormField[];
}

export type FormData = {
  [key: string]: any;
};

/** A document is complete only after its active database row is confirmed. */
export const validateDocument = (value: any): boolean => {
  if (Array.isArray(value)) return value.length > 0 && value.every(isSavedDocument);
  return isSavedDocument(value);
};


/**
 * Full formSections, re-exported from the extracted initial-submission schema.
 *
 * This is a LAZY re-export binding (read at access time, not module-load time).
 * That matters because initialSubmissionForm.ts imports `validateDocument` from
 * this file, so the two modules form a benign import cycle; a lazy binding makes
 * the value resolve correctly regardless of which module the bundler evaluates
 * first.
 */
export { initialSubmissionFormSections as formSections } from '../forms/initialSubmissionForm';

/** Dependencies check logic. */
function checkSingleDependency(
  watchValues: any,
  dependency: { field: string; value: string }
): boolean {
  const currentValue = watchValues[dependency.field];

  // Handle array values (checkboxes)
  if (Array.isArray(currentValue)) {
    return currentValue.includes(dependency.value);
  }

  // Handle single values (radio, text, etc.)

  return currentValue === dependency.value;
}

function shouldDisplayField(watchValues: any, dependency?: Record<string, any>): boolean {
  if (!dependency) return true;

  if (dependency.field && dependency.value) {
    return checkSingleDependency(watchValues, dependency as { field: string; value: string });
  }

  if (dependency.not) {
    const isSatisfied = checkSingleDependency(watchValues, dependency.not);
    return !isSatisfied;
  }

  if (dependency.all && Array.isArray(dependency.all)) {
    return dependency.all.every((cond: { field: string; value: string }) =>
      checkSingleDependency(watchValues, cond)
    );
  }

  return true;
}

/** Helper function to render HTML content safely */
function renderHTML(content: string, className?: string) {
  return (
    <span
      className={className}
      dangerouslySetInnerHTML={{ __html: content }}
    />
  );
}

/** Props for FormSubmissionFields */
interface FormSubmissionFieldsProps {
  onSubmit: (data: FormData) => void;
  register: ReturnType<typeof useForm<FormData>>['register'];
  errors: FieldErrorsImpl<FormData>;
  watchAll: FormData;
  setValue: ReturnType<typeof useForm<FormData>>['setValue'];
  handleImmediateAutosave: () => void;
  handleSubmit: ReturnType<typeof useForm<FormData>>['handleSubmit'];
  savingStatus: 'idle' | 'saving' | 'saved' | 'error';
  loading: boolean;
  sectionRefs?: React.RefObject<HTMLDivElement>[];
  submissionId?: string | null;
  userId?: string;
  onDocumentsPendingChange?: (pending: boolean) => void;
  /** Whether to hide the submit button */
  hideSubmitButton?: boolean;
  /**
   * Optional schema override. Defaults to the initial-submission formSections so
   * every existing caller is unchanged; the continuing-review pages pass the CR
   * schema here to reuse this renderer.
   */
  sections?: FormSection[];
  /** Optional form title. Defaults to the initial-submission heading. */
  title?: string;
}

/** Component that encapsulates the entire IRB form. */
export const FormSubmissionFields: React.FC<FormSubmissionFieldsProps> = ({
  onSubmit,
  register,
  errors,
  watchAll,
  setValue,
  handleImmediateAutosave,
  handleSubmit,
  savingStatus,
  loading,
  sectionRefs,
  submissionId,
  userId,
  onDocumentsPendingChange,
  hideSubmitButton,
  sections = initialSubmissionFormSections,
  title = 'New Study Submission Form',
}) => {
  const [documentsPending, setDocumentsPending] = useState(Boolean(submissionId));
  const current = useRef({ watchAll, handleImmediateAutosave, onDocumentsPendingChange });
  current.current = { watchAll, handleImmediateAutosave, onDocumentsPendingChange };
  const fields = useMemo(() => sections.flatMap(section => section.items).filter(field => field.itemType === 'document' && field.name), [sections]);
  const categories = fields.map(field => ({
    name: field.name!, label: USER_DOCUMENT_TYPES[field.name!] || field.name!,
    required: shouldDisplayField(watchAll, field.dependency), instructions: field.label,
  }));
  const workspaceIndex = sections.findIndex(section => /documents/i.test(section.heading));
  useEffect(() => {
    if (!submissionId) return;
    fields.forEach(field => register(field.name!, { validate: value =>
      !shouldDisplayField(current.current.watchAll, field.dependency) || validateDocument(value) || `Please upload ${USER_DOCUMENT_TYPES[field.name!] || 'the required document'}.`,
    }));
  }, [register, fields, submissionId]); // schema is stable; validation reads current answers
  const syncDocuments = (documents: import('../services/docStorageService').DocumentMetadata[]) => {
    const values = documentFormValues(documents, current.current.watchAll);
    Object.entries(values).forEach(([key, value]) => setValue(key, value, { shouldValidate: true }));
    // Let react-hook-form publish the new values before requesting an autosave.
    setTimeout(() => current.current.handleImmediateAutosave(), 0);
  };
  const pendingChanged = (pending: boolean) => {
    setDocumentsPending(pending);
    current.current.onDocumentsPendingChange?.(pending);
  };

  // Basic styling classes
  const labelClasses = 'block text-base font-[Inter] text-[#343a40] font-medium mb-2';
  const inputClasses =
    'w-full bg-white border border-[#343a40] rounded-md px-3 py-2 text-base focus:outline-none focus:ring-2 focus:ring-[#F55D3E]';
  const errorClasses = 'mt-1 text-sm font-[Inter] text-[#B83EF5]';

  // Check if a field is part of the PI information section
  const isPIInfoField = (fieldName?: string) => {
    if (!fieldName) return false;
    const pi_billing_Fields = [
      'pi_first_name', 'pi_last_name', 'pi_degree', 'pi_email', 'pi_profile_links',
      'pi_phone', 'pi_organization', 'pi_country', 'pi_country_non-us', 'pi_address_line_1',
      'pi_address_line_2', 'pi_city', 'pi_state', 'pi_zip_code', 'pi_npi_number', 'is_pi_physician',
      'invoice_email', 'billing_contact_first_name', 'billing_contact_last_name', 'billing_contact_phone'
    ];
    return pi_billing_Fields.includes(fieldName);
  };

  // Get grid column classes based on field name
  const getGridColumnClass = (fieldName?: string) => {
    if (!fieldName) return 'col-span-2';

    // Field pairs that should be side-by-side
    if (fieldName === 'pi_first_name' || fieldName === 'pi_last_name') {
      return 'col-span-1';
    }
    if (fieldName === 'pi_degree' || fieldName === 'pi_organization') {
      return 'col-span-1';
    }
    if (fieldName === 'pi_email' || fieldName === 'pi_phone') {
      return 'col-span-1';
    }
    if (fieldName === 'pi_city' || fieldName === 'pi_state') {
      return 'col-span-1';
    }
    if (fieldName === 'pi_address_line_1' || fieldName === 'pi_address_line_2') {
      return 'col-span-1';
    }
    if (fieldName === 'pi_zip_code' || fieldName === 'pi_country') {
      return 'col-span-1';
    }
    if (fieldName === 'invoice_email' || fieldName === 'billing_contact_phone') {
      return 'col-span-1';
    }
    if (fieldName === 'billing_contact_first_name' || fieldName === 'billing_contact_last_name') {
      return 'col-span-1';
    }
    // Default to full width
    return 'col-span-2';
  };

  // Renders a single form field based on its type
  const renderField = (field: FormField) => {
    const currentValue = field.name ? watchAll[field.name] : '';

    switch (field.fieldType) {
      case 'text': {
        // Extract maxLength from validation and calculate remaining characters
        const maxLength = field.validation?.maxLength;
        const currentLength = typeof currentValue === 'string' ? currentValue.length : 0;

        return (
          <div className="mb-4" key={field.name}>
            <div className="flex justify-between items-center">
              <label className={labelClasses}>{renderHTML(field.label)}</label>
              {maxLength !== undefined && (
                <span className={`text-sm ${currentLength > maxLength
                  ? 'text-red-500'
                  : currentLength > maxLength * 0.8
                    ? 'text-amber-600'
                    : 'text-gray-500'
                  }`}>
                  {currentLength}/{maxLength}
                </span>
              )}
            </div>

            {field.rows && field.rows > 1 ? (
              <textarea
                placeholder={field.placeholder}
                className={inputClasses}
                rows={field.rows}
                {...register(field.name ?? '', field.validation)}
              />
            ) : (
              <input
                placeholder={field.placeholder}
                className={inputClasses}
                {...register(field.name ?? '', field.validation)}
              />
            )}
            {errors[field.name ?? ''] && (
              <span className={errorClasses}>Invalid input.</span>
            )}
          </div>
        );
      }
      case 'radio': {
        // Yes/No radios inline
        if (
          field.options &&
          field.options.length === 2 &&
          field.options.every(opt => ['Yes', 'No'].includes(opt))
        ) {
          return (
            <div className="mb-4 flex items-center justify-between" key={field.name}>
              <label
                className={labelClasses + ' mb-0'}
                dangerouslySetInnerHTML={{ __html: field.label }}
                style={{ flex: 1, minWidth: 0 }}
              />
              <div className="flex items-center gap-x-6" style={{ minWidth: 120, justifyContent: 'flex-end' }}>
                {field.options.map(opt => (
                  <label key={opt} className="inline-flex items-center gap-x-1">
                    <input
                      type="radio"
                      {...register(field.name ?? '', { required: field.validation?.required })}
                      value={opt}
                      checked={currentValue === opt}
                      onChange={e => {
                        setValue(field.name ?? '', opt, { shouldValidate: true });
                        handleImmediateAutosave();
                      }}
                      className="form-radio h-4 w-4"
                    />
                    <span className="text-sm">{opt}</span>
                  </label>
                ))}
              </div>
              {errors[field.name ?? ''] && (
                <span className="ml-3 text-xs text-red-500">Required.</span>
              )}
            </div>
          );
        }
        // fallback to existing rendering for other radios (leave as-is for now)
        break;
      }

      case 'dropdown-single':
        if (!field.options) return null;
        return (
          <div className="mb-6" key={field.name}>
            <Listbox
              value={currentValue || ''}
              onChange={(val: any) => {
                setValue(field.name ?? '', val, { shouldValidate: true });
                handleImmediateAutosave();
              }}
            >
              <Listbox.Label className={labelClasses}>{renderHTML(field.label)}</Listbox.Label>
              <div className="relative mt-1">
                <Listbox.Button
                  className="relative w-full cursor-pointer bg-white border border-[#343a40] rounded-md px-3 py-2 text-left focus:outline-none focus:ring-2 focus:ring-[#F55D3E]"
                >
                  <span className="block truncate">{currentValue || '--Select--'}</span>
                </Listbox.Button>
                <div className="relative mt-1">
                  <Listbox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white border border-[#343a40]">
                    {field.options.map((opt) => (
                      <Listbox.Option
                        key={opt}
                        value={opt}
                        className={({ active }) =>
                          `cursor-pointer select-none relative py-2 pl-3 pr-4 ${active ? 'bg-[#F9A594] text-[#343a40]' : 'text-[#343a40]'
                          }`
                        }
                      >
                        {opt}
                      </Listbox.Option>
                    ))}
                  </Listbox.Options>
                </div>
              </div>
            </Listbox>
            {errors[field.name ?? ''] && (
              <span className={errorClasses}>Required.</span>
            )}
          </div>
        );

      case 'dropdown-multiple':
        if (!field.options) return null;
        const multiValue = Array.isArray(currentValue) ? currentValue : [];
        return (
          <div className="mb-6" key={field.name}>
            <Listbox
              multiple
              value={multiValue}
              onChange={(val: any) => {
                setValue(field.name ?? '', val, { shouldValidate: true });
                handleImmediateAutosave();
              }}
            >
              <Listbox.Label className={labelClasses}>{renderHTML(field.label)}</Listbox.Label>
              <div className="relative mt-1">
                <Listbox.Button
                  className="relative w-full cursor-pointer bg-white border border-[#343a40] rounded-md px-3 py-2 text-left focus:outline-none focus:ring-2 focus:ring-[#F55D3E]"
                >
                  <span className="block truncate">
                    {multiValue.length > 0 ? multiValue.join(', ') : '--Select--'}
                  </span>
                </Listbox.Button>
                <div className="relative mt-1">
                  <Listbox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white border border-[#343a40]">
                    {field.options.map((opt) => {
                      const selected = multiValue.includes(opt);
                      return (
                        <Listbox.Option
                          key={opt}
                          value={opt}
                          className={({ active }) =>
                            `cursor-pointer select-none relative py-2 pl-3 pr-4 ${active ? 'bg-[#F9A594] text-[#343a40]' : 'text-[#343a40]'
                            }`
                          }
                        >
                          <span>{opt}</span>
                          {selected && (
                            <span className="absolute inset-y-0 right-0 flex items-center pr-3 text-green-500">
                              ✓
                            </span>
                          )}
                        </Listbox.Option>
                      );
                    })}
                  </Listbox.Options>
                </div>
              </div>
            </Listbox>
            {errors[field.name ?? ''] && (
              <span className={errorClasses}>Required.</span>
            )}
          </div>
        );

      case 'checkboxes':
        if (!field.options) return null;
        return (
          <div className="mb-6" key={field.name}>
            <label className={labelClasses}>{renderHTML(field.label)}</label>
            {field.options.map((opt) => {
              const checkedArr = watchAll[field.name ?? ''] || [];
              const isChecked = checkedArr.includes(opt);

              const handleCheckboxChange = (evt: React.ChangeEvent<HTMLInputElement>) => {
                const valArray = Array.isArray(checkedArr) ? [...checkedArr] : [];
                if (evt.target.checked) {
                  valArray.push(opt);
                } else {
                  const idx = valArray.indexOf(opt);
                  if (idx !== -1) valArray.splice(idx, 1);
                }
                setValue(field.name ?? '', valArray, { shouldValidate: true });
                handleImmediateAutosave();
              };

              return (
                <div key={opt} className="flex items-center mt-2">
                  <input
                    type="checkbox"
                    checked={isChecked}
                    onChange={handleCheckboxChange}
                    className="h-4 w-4 text-[#F55D3E] border-[#343a40] rounded focus:ring-0 mr-2"
                  />
                  <span className="text-base font-[Inter] text-[#343a40]">{opt}</span>
                </div>
              );
            })}
            {errors[field.name ?? ''] && (
              <span className={errorClasses}>Required.</span>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <form
      onSubmit={(e) => {
        if (documentsPending) { e.preventDefault(); return; }
        if (Object.keys(errors).length > 0) {
          // Create a mapping of field names to their labels
          const nameToLabelMap: Record<string, string> = {};

          // Iterate through all form sections and fields to build the mapping
          sections.forEach(section => {
            section.items.forEach(field => {
              if (field.name) {
                nameToLabelMap[field.name] = field.label;
              }
            });
          });

          // Map error field names to their labels, fallback to field name if label not found
          const errorLabels = Object.keys(errors).map(fieldName =>
            nameToLabelMap[fieldName] || fieldName
          );

          alert(`Please complete the following required fields:\n${errorLabels.join('\n')}`);
        }
        return handleSubmit(onSubmit)(e);
      }}
      className="bg-[#f8f9fa] p-6 rounded-md max-w-4xl mx-auto my-8 font-[Inter]"
    >
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-[#F55D3E] mb-2">{title}</h1>
        <p className="text-sm text-[#343a40]">
          This form shouldn't take more than 10 minutes to complete if you have study information ready.
          We'll continuously save your progress as you're working, and you may access this submission from your dashboard if you exit this page.
        </p>
      </div>

      {sections.map((section, sIdx) => (
        <div
          key={sIdx}
          className="mb-8 pt-[80px] mt-[-80px]"
          ref={sectionRefs ? sectionRefs[sIdx] : null}
        >
          {section.heading && (
            <h2 className="text-xl font-semibold text-[#F55D3E] mb-4">{section.heading}</h2>
          )}
          {section.description && (
            <p className="text-base text-[#343a40] mb-4 whitespace-pre-line">{section.description}</p>
          )}
          {/* Check if this section has PI info fields and wrap in grid if needed */}
          {section.items.some(item => item.name && isPIInfoField(item.name)) && (
            <div className="grid grid-cols-2 gap-x-4 gap-y-2 mt-2 mb-4">
              {section.items.map((item, iIdx) => {
                if (!shouldDisplayField(watchAll, item.dependency) || !isPIInfoField(item.name)) {
                  return null;
                }

                if (item.itemType === 'question' && item.name) {
                  return (
                    <div key={item.name} className={`${getGridColumnClass(item.name)} mb-2`}>
                      {renderField(item)}
                    </div>
                  );
                }
                return null;
              })}
            </div>
          )}

          {/* Regular (non-PI) fields */}
          {section.items.map((item, iIdx) => {
            if (item.itemType === 'document' || item.name === 'doc_selection' || !shouldDisplayField(watchAll, item.dependency) || (item.name && isPIInfoField(item.name))) {
              return null;
            }
            if (item.itemType === 'question' && item.name) {
              return renderField(item);
            } else if (item.itemType === 'warning') {
              return (
                <div
                  key={`${sIdx}-${iIdx}`}
                  className="border-l-4 border-[#B83EF5] bg-[#f8f9fa] p-4 mb-6"
                >
                  <strong className="text-[#B83EF5] text-2xl">⚠️</strong>{' '}
                  <span className="text-[#343a40]">{renderHTML(item.label)}</span>
                </div>
              );
            } else if (item.itemType === 'instruction') {
              return (
                <div
                  key={`${sIdx}-${iIdx}`}
                  className="border-l-4 border-[#F9A594] bg-white p-4 mb-6"
                >
                  <strong className="text-[#343a40] text-2xl">ℹ</strong>{' '}
                  <span className="text-[#343a40]">{renderHTML(item.label)}</span>
                </div>
              );
            }
            return null;
          })}
          {sIdx === workspaceIndex && submissionId && userId && <DocumentUploadWorkspace
            submissionId={submissionId} userId={userId} categories={categories}
            onDocumentsChange={syncDocuments} onPendingChange={pendingChanged}
          />}
        </div>
      ))}

      {/* Only render the submit button if not hidden */}
      {!hideSubmitButton && (
        <button
          type="submit"
          disabled={documentsPending}
          className="disabled:opacity-50 inline-flex items-center justify-center bg-[#F55D3E] text-white rounded-md px-4 py-2 font-semibold hover:bg-[#d94a30] focus:outline-none focus:ring-2 focus:ring-[#F55D3E]"
        >
          Submit
        </button>
      )}
    </form>
  );
};
